import { Fragment, useEffect, useState } from 'react';
import { Ban, ChevronDown, ChevronRight, RefreshCw, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import apiClient from '../api/client';
import { useToast } from './ui/Toast';
import { Button } from './ui/Button';

interface CatNode {
  id: string;
  /** The category's stable code (GA-…, UNI-…, CUST-…) — what the counter's tabs reference. */
  code: string;
  name: string;
  source: 'TECDOC' | 'CUSTOM';
  kind: 'NODE' | 'GENERIC' | null;
  hasChildren: boolean;
}

/**
 * Modal lazy tree-picker for attaching a product to a category. The TecDoc tree has thousands of
 * nodes, so children load on demand. Clicking a category's name selects it; generic articles (the
 * intended leaf attach target) are highlighted.
 */
export function CategoryPicker({
  onPick,
  onClose,
}: {
  onPick: (id: string | null, name: string, code?: string) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { showApiError } = useToast();
  const [roots, setRoots] = useState<CatNode[] | null>(null);
  const [childrenMap, setChildrenMap] = useState<Record<string, CatNode[]>>({});
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [loadingNode, setLoadingNode] = useState<Set<string>>(new Set());

  // activeOnly: a category staff deactivated is not a filing target, and because the tree loads
  // one level at a time its whole subtree disappears with it.
  const load = async (parentId: string | null) => {
    const { data } = await apiClient.get<CatNode[]>(
      `/products/categories/tree?activeOnly=1${parentId ? `&parentId=${parentId}` : ''}`
    );
    if (parentId === null) setRoots(data);
    else setChildrenMap((m) => ({ ...m, [parentId]: data }));
  };

  useEffect(() => {
    load(null).catch(showApiError);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = async (n: CatNode) => {
    if (expanded.has(n.id)) {
      setExpanded((s) => {
        const x = new Set(s);
        x.delete(n.id);
        return x;
      });
      return;
    }
    if (!childrenMap[n.id]) {
      setLoadingNode((s) => new Set(s).add(n.id));
      try {
        await load(n.id);
      } catch (err) {
        showApiError(err);
      } finally {
        setLoadingNode((s) => {
          const x = new Set(s);
          x.delete(n.id);
          return x;
        });
      }
    }
    setExpanded((s) => new Set(s).add(n.id));
  };

  const renderNodes = (nodes: CatNode[], depth: number): React.ReactNode =>
    nodes.map((n) => (
      <Fragment key={n.id}>
        <div
          className="flex items-center gap-1.5 rounded px-1 py-1 text-sm hover:bg-muted/40"
          style={{ paddingLeft: depth * 18 + 4 }}
        >
          {n.hasChildren ? (
            <button
              onClick={() => toggle(n)}
              className="text-muted-foreground"
              aria-label={t('admin.categoryPicker.expand')}
            >
              {loadingNode.has(n.id) ? (
                <RefreshCw className="h-3.5 w-3.5 animate-spin" />
              ) : expanded.has(n.id) ? (
                <ChevronDown className="h-3.5 w-3.5" />
              ) : (
                <ChevronRight className="h-3.5 w-3.5" />
              )}
            </button>
          ) : (
            <span className="inline-block w-3.5" />
          )}
          <button
            onClick={() => onPick(n.id, n.name, n.code)}
            className="text-left hover:text-primary hover:underline"
          >
            {n.name}
          </button>
          {n.kind === 'GENERIC' && (
            <span className="text-[10px] px-1 rounded bg-primary/10 text-primary">
              {t('admin.categoryPicker.genericArticle')}
            </span>
          )}
        </div>
        {expanded.has(n.id) && childrenMap[n.id] && renderNodes(childrenMap[n.id], depth + 1)}
      </Fragment>
    ));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="flex max-h-[80vh] w-full max-w-lg flex-col rounded-lg border bg-card shadow-lg">
        <div className="flex items-center justify-between border-b px-4 py-2">
          <h2 className="text-sm font-semibold">{t('admin.categoryPicker.title')}</h2>
          <Button size="sm" variant="ghost" onClick={() => onPick(null, '')}>
            <Ban className="h-4 w-4" />
            {t('admin.categoryPicker.none')}
          </Button>
        </div>
        <div className="flex-1 overflow-y-auto p-3">
          {roots === null ? (
            <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
          ) : roots.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('admin.categoryPicker.empty')}</p>
          ) : (
            renderNodes(roots, 0)
          )}
        </div>
        <div className="flex justify-end border-t px-4 py-2">
          <Button size="sm" variant="outline" onClick={onClose}>
            <X className="h-4 w-4" />
            {t('common.close')}
          </Button>
        </div>
      </div>
    </div>
  );
}
